// filepath: c:\Users\Puneet Bajaj\Desktop\Ankit\ankitnewform\src\features\simulationUseCase\UseCaseSelector.tsx
import React, { useState, useEffect } from 'react';
import {
  PenTool,
  // TestTube,  // Commented out - concept-test hidden
  BarChart,
  DollarSign,
  Target,
  ArrowLeft,
  SearchCheck,
  Lock,
  TrendingUp,
  MousePointer,
  Package,
  Users,
  ClipboardList,
  Lightbulb,
  Search,
  Settings,
  // Image,  // Commented out - logo_test hidden
  MessageCircle
} from 'lucide-react';
import StepContainer from '../../components/StepContainer';
import SimulationCard from '../../components/SimulationCard';
import Button from '../../components/Button';
import ContentCreationForm from './useCaseForms/ContentCreationForm';
import ContentTestingForm from './useCaseForms/ContentTestingForm';
import InsightsForm from './useCaseForms/InsightsForm';
import PricingForm from './useCaseForms/PricingForm';
import ChannelStrategyForm from './useCaseForms/ABTestCreatives';
import ImageSurveyForm from './useCaseForms/ImageSurvey';
import ABTestCreativesFormGemini from './useCaseForms/ABTestCreativesGemini';
// import PackagingReviewGemini from './useCaseForms/PackagingReviewGemini';
import ABTestCreativePackagingForm from './useCaseForms/ABTestCreativePackaging';
import PackagingReviewV2Form from './useCaseForms/PackagingReviewV2Form';
import AttributionForm from './useCaseForms/AttributionForm';
import BuyerInsightsForm from './useCaseForms/BuyerInsightsForm';
import SurveyAndFocusGroups from './useCaseForms/SurveyAndFocusGroups';
import TestUseCase from './useCaseForms/TestUseCase';
import LogoTest from './useCaseForms/LogoTest';
import LongContentCreationForm from './useCaseForms/LongContentCreationForm';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import UnifiedSegmentsSelector from '../../components/segments/UnifiedSegmentsSelector';
import { useAudience } from '../../context/AudienceContext';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { LockedFeature } from '../../components/subscription/UpgradePrompt';
import SimulationHistoryPanel from '../../components/SimulationHistoryPanel';
import { useNavigate } from 'react-router-dom';
import CreateCampaignStratergy from './useCaseForms/CreateCampaignStratergy';
// import AmazonSearchForm from './useCaseForms/AmazonSearchForm';
import ConceptTestForm from './useCaseForms/ConceptTestForm';
import { getZIndexClass } from '../../config/zIndex';

// Interface to track persona filters within each segment
export interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

export type UseCaseType =
  | 'create-content'
  | 'content-testing'
  | 'get-insights'
  | 'pricing-analysis'
  | 'channel-strategy'
  | 'image-survey'
  | 'ab-test-creatives-gemini'
  | 'packaging-review-gemini'
  // | 'packaging-review-v2'
  | 'attribution'
  | 'buyer-insights-report'
  | 'survey-and-focus-groups'
  | 'test-use-case'
  | 'create-campaign-strategy'
  | 'amazon-search'
  | 'logo_test'
  | 'concept-test'
  | 'hbs-chat'
  | null;

// Define the possible steps in the flow
type FlowStep = 'segment-selection' | 'use-case-selection' | 'form-filling' | 'results-view';

interface UseCaseSelectorProps {
  onComplete: (simulationId: number) => void; // Modified to pass the simulationId
  onBack: () => void;
  audienceId: number;
  onStepChange?: (step: FlowStep) => void;
  onEditStep?: () => void; // New prop to signal edits
  currentStep?: FlowStep;
  hasEditedStep?: boolean; // New prop to track edit state from parent
  onFormDataChange?: (useCaseType: string, data: Record<string, any>) => void;
}

const UseCaseSelector: React.FC<UseCaseSelectorProps> = ({
  onComplete,
  onBack,
  audienceId,
  onStepChange,
  onEditStep,
  currentStep: providedStep,
  hasEditedStep = false,
  onFormDataChange,
}) => {
  const { audienceData, updateAudienceData } = useAudience();
  const { isAdmin, isAetvUser, isHbsUser } = useAuth();
  const { hasFeatureAccess, currentSubscription } = useSubscription();
  const navigate = useNavigate();  
  
  // Define which use cases are "coming soon"
  const COMING_SOON_USE_CASES: UseCaseType[] = [
    // 'get-insights',
    // 'pricing-analysis', 
    // 'attribution',
    // 'channel-strategy'
  ];

  // Define restricted use cases for AETV users (only these will be visible)
  const AETV_ALLOWED_USE_CASES: UseCaseType[] = [
    'survey-and-focus-groups',     // Surveys (Qual and Quant)
    'attribution',                 // AB Test Messaging
    'image-survey',               // Creative Image Click/Rank Test
  ];

  // Define restricted use cases for HBS users (mba2026.hbs.edu domain)
  const HBS_ALLOWED_USE_CASES: UseCaseType[] = [
    'packaging-review-v2',        // Packaging Review
    'image-survey',               // Creative Rank Test
    'survey-and-focus-groups',    // Surveys (Qual and Quant)
    'hbs-chat',                   // Chat - opens in new tab
  ];
  // Define available use cases FIRST before any functions that use it
  const useCases = [
    // Available use cases
    // {
    //   type: 'create-content' as UseCaseType,
    //   icon: <PenTool className="w-5 h-5" />,
    //   title: 'Create Content',
    //   description: 'Generate content, messages, ads, emails and more tailored to your audience',
    // },
    // {
    //   type: 'content-testing' as UseCaseType,
    //   icon: <TestTube className="w-5 h-5" />,
    //   title: 'Test Content',
    //   description: 'Run A/B or multivariate tests on any content, creatives and more',
    // },
    {
      type: 'channel-strategy' as UseCaseType,
      icon: <TrendingUp className="w-5 h-5" />,
      title: 'A/B Test or Review Creatives and Ads',
      description: 'Instantly predict which creative will perform best, get insights on what resonates and what does not',
    },
    {
      type: 'image-survey' as UseCaseType,
      icon: <MousePointer className="w-5 h-5" />,
      title: 'Creative Rank Test',
      description: 'Run a ranked click test to identify the best creatives for your listing',
    },
    // {
    //   type: 'ab-test-creatives-gemini' as UseCaseType,
    //   icon: <Share2 className="w-5 h-5" />,
    //   title: 'A/B Test Creatives, Ads ',
    //   description: 'Discover the best way to reach this audience with detailed  execution plan'
    // },
    // {
    //   type: 'packaging-review-gemini' as UseCaseType,
    //   icon: <Package className="w-5 h-5" />,
    //   title: 'Packaging Review',
    //   description: 'Review and optimize your packaging design',
    // },
    {
      type: 'packaging-review-v2' as UseCaseType,
      icon: <Package className="w-5 h-5" />,
      title: 'Packaging Review',
      description: 'Advanced packaging analysis with technical compliance, shelf test, and persona reactions',
    },
    // {
    //   type: 'buyer-insights-report' as UseCaseType,
    //   icon: <Users className="w-5 h-5" />,
    //   title: 'Customer Insights Report',
    //   description: 'Understand key buying triggers, decision and success criteria, concerns and buying journey of your customers',
    // },

    // // Coming soon use cases - placed at the end
    // {
    //   type: 'get-insights' as UseCaseType,
    //   icon: <BarChart className="w-5 h-5" />,
    //   title: 'Get Insights',
    //   description: 'Simulate a survey or focus group to get insights',
    // },
    // {
    //   type: 'pricing-analysis' as UseCaseType,
    //   icon: <DollarSign className="w-5 h-5" />,
    //   title: 'Pricing Analysis',
    //   description: 'Test how price points perform across different segments',
    // },
    {
      type: 'attribution' as UseCaseType,
      icon: <Target className="w-5 h-5" />,
      title: 'AB Test Messaging',
      description: 'Learn what drives or blocks conversions',
    },
    {
      type: 'survey-and-focus-groups' as UseCaseType,
      icon: <ClipboardList className="w-5 h-5" />,
      title: 'Surveys (Qual and Quant)',
      description: 'Run qualitative and quantitative interviews to get deep insights',
    },
    // {
    //   type: 'create-campaign-strategy' as UseCaseType,
    //   icon: <Lightbulb className="w-5 h-5" />,
    //   title: 'Create Campaign Concepts',
    //   description: 'Explore new ideas based on your audience needs and motivations',
    // },
    // {
    //   type: 'amazon-search' as UseCaseType,
    //   icon: <Search className="w-5 h-5" />,
    //   title: 'Amazon Search Click Test',
    //   description: 'See how your listing performs against the competition in amazon search results',
    // },
    // {
    //   type: 'logo_test' as UseCaseType,
    //   icon: <Image className="w-5 h-5" />,
    //   title: 'Logo Test',
    //   description: 'Test and analyze logo effectiveness with your audience',
    // },
    // {
    //   type: 'concept-test' as UseCaseType,
    //   icon: <TestTube className="w-5 h-5" />,
    //   title: 'Concept Test',
    //   description: 'Test product concepts with AI-simulated consumer to predict purchase intent',
    // },
    // Test use case - only visible to admins
    ...(isAdmin ? [{
      type: 'test-use-case' as UseCaseType,
      icon: <Settings className="w-5 h-5" />,
      title: 'Test Use Case',
      description: 'Simulate a test to get insights',
    }] : []),
    // Chat use case - only visible to HBS users
    ...(isHbsUser ? [{
      type: 'hbs-chat' as UseCaseType,
      icon: <MessageCircle className="w-5 h-5" />,
      title: 'Chat',
      description: 'Chat with the Natural Organic Shoppers audience',
    }] : []),
  ];

  // Initialize with provided step or default to segment selection
  const [localCurrentStep, setLocalCurrentStep] = useState<FlowStep>(
    providedStep || 'segment-selection'
  );

  // Use either the provided step from props or our local state
  const currentStep = providedStep || localCurrentStep;

  // Determine initial use case from audience data
  const getInitialUseCase = (): UseCaseType => {
    if (!audienceData.selectedUseCase) return null;

    const matchingUseCase = useCases.find(uc =>
      uc.title.toLowerCase() === audienceData.selectedUseCase?.toLowerCase()
    );

    return matchingUseCase?.type || null;
  };

  // Initialize from context if available, or use previously saved values
  const [selectedUseCase, setSelectedUseCase] = useState<UseCaseType>(getInitialUseCase());
  const [selectedSegments, setSelectedSegments] = useState<number[]>(
    audienceData.selectedSegments || []
  );
  const [personaFilters, setPersonaFilters] = useState<Record<number, SegmentPersonaFilters>>(
    audienceData.personaFilters || {}
  );
  const [simulationId, setSimulationId] = useState<number | null>(null);
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [localHasEditedStep, setLocalHasEditedStep] = useState<boolean>(hasEditedStep);

  // Store form data between steps
  const [formState, setFormState] = useState<Record<string, any>>({});

  // Update selectedUseCase when audienceData changes
  useEffect(() => {
    const initialUseCase = getInitialUseCase();
    if (initialUseCase && !selectedUseCase) {
      setSelectedUseCase(initialUseCase);
    }
  }, [audienceData.selectedUseCase]);

  // Track edit state from parent
  useEffect(() => {
    setLocalHasEditedStep(hasEditedStep);
  }, [hasEditedStep]);

  // Cleanup on unmount - commented out for now as it may not be needed
  // and can cause issues with state management
  useEffect(() => {
    return () => {
      // Cleanup if needed in future
    };
  }, []);

  // Update local step and notify parent component
  const setCurrentStep = (step: FlowStep) => {

    // Check if we're trying to move forward while having edited
    if (localHasEditedStep) {
      const stepOrder: FlowStep[] = ['segment-selection', 'use-case-selection', 'form-filling', 'results-view'];
      const currentStepIndex = stepOrder.indexOf(currentStep);
      const targetStepIndex = stepOrder.indexOf(step);

      // If trying to move forward (skip steps) after editing, prevent it
      if (targetStepIndex > currentStepIndex + 1) {
        return; // Prevent navigation
      }
    }

    // Check if we're moving backward - if so, allow it regardless of edit state
    const stepOrder: FlowStep[] = ['segment-selection', 'use-case-selection', 'form-filling', 'results-view'];
    const isMovingBackward = stepOrder.indexOf(step) < stepOrder.indexOf(currentStep);

    if (isMovingBackward) {
      // If moving backward, reset local edit state for that step
      setLocalHasEditedStep(false);
    }

    // When changing steps, update the selected use case if appropriate
    if (step === 'form-filling' && !selectedUseCase && audienceData.selectedUseCase) {
      // Find the use case type from its title
      const matchingUseCase = useCases.find(uc => uc.title === audienceData.selectedUseCase);
      if (matchingUseCase) {
        setSelectedUseCase(matchingUseCase.type);
      }
    }

    setLocalCurrentStep(step);
    if (onStepChange) {
      onStepChange(step);
    }
  };

  // Store form data to persist across navigation
  const updateFormState = (formType: UseCaseType, data: Record<string, any>) => {
    const key = formType as string;

    // Check if data has actually changed before updating state
    if (JSON.stringify(formState[key]) === JSON.stringify(data)) {
      return;
    }

    setFormState(prev => ({
      ...prev,
      [key]: data
    }));

    // Also notify the parent component
    if (onFormDataChange && formType) {
      onFormDataChange(formType as string, data);
    }
  };

  // Keep in sync with prop changes
  useEffect(() => {
    if (providedStep && providedStep !== localCurrentStep) {
      setLocalCurrentStep(providedStep);
    }
  }, [providedStep, localCurrentStep]);

  // Handle simulation history selection
  const handleSelectHistorySimulation = (simulationId: number) => {
    navigate(`/simulation-results/${simulationId}`);
    setIsHistoryPanelOpen(false);
    onComplete(simulationId); // Call onComplete when navigating to results
  };

  // Handle segment selection
  const handleSegmentSelect = (segments: number[], filters: Record<number, SegmentPersonaFilters>) => {

    setSelectedSegments(segments);
    setPersonaFilters(filters);

    // Update audience context
    updateAudienceData({
      selectedSegments: segments,
      personaFilters: filters
    });

    // Move to use case selection step using both local state and parent notification

    // Update local state
    setLocalCurrentStep('use-case-selection');

    // Also directly notify parent about step change
    if (onStepChange) {
      onStepChange('use-case-selection');
    }
  };

  // Handle form submission with simulation ID
  const handleFormSubmit = (simId: number) => {
    setSimulationId(simId);
    setCurrentStep('results-view');
    // When navigating to results view, tell the parent we're moving forward normally 
    if (onStepChange) {
      onStepChange('results-view');
    }
    // Pass the simulation ID to the parent
    onComplete(simId);
  };

  // Handle form edit
  const handleFormEdit = () => {
    // Notify parent about edit
    if (onEditStep) {
      onEditStep();
      setLocalHasEditedStep(true);
    }
  };  // Handle use case selection
  const handleUseCaseSelect = (useCase: UseCaseType) => {
    // Check if this is one of the "coming soon" use cases
    const isComingSoon = COMING_SOON_USE_CASES.includes(useCase);

    // Don't proceed if it's a coming soon use case
    if (isComingSoon) {
      return;
    }

    // Handle HBS Chat use case - opens in new tab
    if (useCase === 'hbs-chat') {
      window.open('/ask-natural-organic', '_blank');
      return; // Don't proceed to form step
    }

    // Check subscription access for this use case
    if (!isAdmin && !hasFeatureAccess(useCase as string)) {
      // Don't proceed if user doesn't have access
      return;
    }

    setSelectedUseCase(useCase);

    // Update context with selected use case
    if (useCase) {
      const useCaseTitle = useCases.find(uc => uc.type === useCase)?.title || '';
      updateAudienceData({ selectedUseCase: useCaseTitle });
    }

    // Move to form filling step
    setCurrentStep('form-filling');
  };

  // Handle navigation back from use case to segments
  const handleBackFromUseCase = () => {
    setCurrentStep('segment-selection');
    // Reset edit flag when going back
    setLocalHasEditedStep(false);
  };

  // Handle navigation back from form to use case selection
  const handleBackFromForm = () => {
    setCurrentStep('use-case-selection');
    // Reset edit flag when going back
    setLocalHasEditedStep(false);
  };

  // Display a message when navigation is restricted
  const renderEditWarningMessage = () => {
    // if (localHasEditedStep && currentStep !== 'results-view') {
    //   return (
    //     <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-md mb-4 text-sm flex items-center">
    //       <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    //         <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    //       </svg>
    //       {/* You've made changes to this step. Please complete this step to continue. */}
    //     </div>
    //   );
    // }
    return null;
  };
  // Render the appropriate form component based on selected use case
  const renderUseCaseForm = () => {
    // Check if the selected use case is a "coming soon" use case
    if (selectedUseCase && COMING_SOON_USE_CASES.includes(selectedUseCase)) {
      // Go back to use case selection if somehow a coming soon use case was selected
      setCurrentStep('use-case-selection');
      return null;
    }

    // Get saved form data for this use case type
    const savedFormData = formState[selectedUseCase as string] || {};

    switch (selectedUseCase) {
      case 'create-content':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ContentCreationForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('create-content', data)}
            />
          </div>
        );
      case 'content-testing':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ContentTestingForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('content-testing', data)}
            />
          </div>
        );
      case 'get-insights':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <InsightsForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('get-insights', data)}
            />
          </div>
        );
      case 'pricing-analysis':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <PricingForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('pricing-analysis', data)}
            />
          </div>
        );
      case 'channel-strategy':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ChannelStrategyForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('channel-strategy', data)}
            />
          </div>
        );
      case 'ab-test-creatives-gemini':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ABTestCreativesFormGemini
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('ab-test-creatives-gemini', data)}
            />
          </div>
        );
      case 'packaging-review-gemini':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ABTestCreativePackagingForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('packaging-review-gemini', data)}
            />
          </div>
        );
      case 'packaging-review-v2':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <PackagingReviewV2Form
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('packaging-review-v2', data)}
            />
          </div>
        );
      case 'attribution':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <AttributionForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('attribution', data)}
            />
          </div>
        );
      case 'buyer-insights-report':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <BuyerInsightsForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('buyer-insights-report', data)}
            />
          </div>
        );
      case 'survey-and-focus-groups':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <SurveyAndFocusGroups
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('survey-and-focus-groups', data)}
            />
          </div>
        );
      case 'create-campaign-strategy':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <CreateCampaignStratergy
              onSubmit={handleFormSubmit} 
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('create-campaign-strategy', data)}
            />
          </div>
        );
      case 'image-survey':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ImageSurveyForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit} 
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('image-survey', data)}
            />
          </div>
        );
      // case 'amazon-search':
      //   return (
      //     <div className="mx-auto w-[80vw] max-w-4xl">
      //       {renderEditWarningMessage()}
      //       <AmazonSearchForm
      //         onSubmit={handleFormSubmit}
      //         selectedSegmentIds={selectedSegments}
      //         personaFilters={personaFilters}
      //         onBack={handleBackFromForm}
      //         onEditStep={handleFormEdit}
      //         initialFormData={savedFormData}
      //         onFormDataChange={(data) => updateFormState('amazon-search', data)}
      //       />
      //     </div>
      //   );
      case 'logo_test':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <LogoTest
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('logo_test', data)}
            />
          </div>
        );
      case 'test-use-case':
        // Only allow test use case for admin users
        if (!isAdmin) {
          // Redirect back to use case selection if non-admin somehow gets here
          setCurrentStep('use-case-selection');
          return null;
        }
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <TestUseCase
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('test-use-case', data)}
            />
          </div>
        );
      case 'concept-test':
        return (
          <div className="mx-auto w-[80vw] max-w-4xl">
            {renderEditWarningMessage()}
            <ConceptTestForm
              onSubmit={handleFormSubmit}
              selectedSegmentIds={selectedSegments}
              personaFilters={personaFilters}
              onBack={handleBackFromForm}
              onEditStep={handleFormEdit}
              initialFormData={savedFormData}
              onFormDataChange={(data) => updateFormState('concept-test', data)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Render the appropriate step component
  const renderStep = () => {

    switch (currentStep) {
      case 'segment-selection':
        return (
          <UnifiedSegmentsSelector
            audienceId={audienceId}
            btnText="Continue to Use Cases"
            onBack={onBack}
            onNext={handleSegmentSelect}
            onEditStep={handleFormEdit}
          />
        );
      case 'results-view':
        return (
          <div className="w-full">
            {simulationId && (
              <div>
                <div className="mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep('form-filling')}
                    icon={<ArrowLeft className="w-4 h-4 mr-1" />}
                  >
                    Back to form
                  </Button>
                </div>
                <SimulationResultsContent 
                  simulationId={simulationId} 
                  defaultChatOpen={true}
                />
              </div>
            )}
          </div>
        );
      case 'use-case-selection':
        return (
          <StepContainer
            title="What would you like to do today?"
            subtitle={`Pick a use case to get started with ${audienceData.audienceName || 'your selected audience'}.`}
            className="animate-fadeIn"
          >
            {renderEditWarningMessage()}
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackFromUseCase}
                icon={<ArrowLeft className="w-4 h-4 mr-1" />}
              >
                Back to segment selection
              </Button>
            </div>            <div className="grid md:grid-cols-2 gap-4">
              {useCases
                .filter((useCase) => {
                  // If user is HBS user (and not admin), only show allowed use cases
                  if (isHbsUser && !isAdmin) {
                    return HBS_ALLOWED_USE_CASES.includes(useCase.type);
                  }
                  // If user is AETV user (and not admin), only show allowed use cases
                  if (isAetvUser && !isAdmin) {
                    return AETV_ALLOWED_USE_CASES.includes(useCase.type);
                  }
                  // For all other users, show all use cases
                  return true;
                })
                .sort((a, b) => {
                  // First sort by access (accessible first, then locked)
                  const aHasAccess = isAdmin || hasFeatureAccess(a.type);
                  const bHasAccess = isAdmin || hasFeatureAccess(b.type);
                  
                  if (aHasAccess && !bHasAccess) return -1; // a comes first
                  if (!aHasAccess && bHasAccess) return 1;  // b comes first
                  
                  // Then sort "coming soon" use cases to the end within each group
                  const aIsComingSoon = COMING_SOON_USE_CASES.includes(a.type);
                  const bIsComingSoon = COMING_SOON_USE_CASES.includes(b.type);

                  if (aIsComingSoon && !bIsComingSoon) return 1;  // a comes after b
                  if (!aIsComingSoon && bIsComingSoon) return -1; // a comes before b
                  return 0; // maintain original order
                })
                .map((useCase) => {
                  // Check if this is one of the "coming soon" use cases
                  const isComingSoon = COMING_SOON_USE_CASES.includes(useCase.type);
                  
                  // Check subscription access (admins bypass all restrictions)
                  // HBS Chat is always accessible for HBS users (no subscription check needed)
                  const isHbsChatForHbsUser = useCase.type === 'hbs-chat' && isHbsUser;
                  const hasAccess = isAdmin || isHbsChatForHbsUser || hasFeatureAccess(useCase.type);
                  const isLocked = !hasAccess && !isComingSoon;

                  // If locked, wrap in LockedFeature component
                  if (isLocked) {
                    return (
                      <LockedFeature
                        key={useCase.type}
                        feature={useCase.title}
                        requiredPlan={currentSubscription?.id === 'free' ? 'Starter' : 'Higher tier'}
                      >
                        <SimulationCard
                          icon={useCase.icon}
                          title={useCase.title}
                          description={useCase.description}
                          disabled={true}
                          onClick={() => {}}
                        />
                      </LockedFeature>
                    );
                  }

                  return (
                    <SimulationCard
                      key={useCase.type}
                      icon={useCase.icon}
                      title={useCase.title}
                      description={useCase.description}
                      disabled={isComingSoon}
                      comingSoon={isComingSoon}
                      onClick={() => !isComingSoon && handleUseCaseSelect(useCase.type)}
                    />
                  );
                })}
            </div>
          </StepContainer>
        );
      case 'form-filling':
        return renderUseCaseForm();
      default:
        return null;
    }
  };

  return (
    <>
      {renderStep()}

      <SimulationHistoryPanel
        isOpen={isHistoryPanelOpen}
        onClose={() => setIsHistoryPanelOpen(false)}
        onSelectSimulation={handleSelectHistorySimulation}
      />
      {isHistoryPanelOpen && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-30 ${getZIndexClass('overlay')}`}
          onClick={() => setIsHistoryPanelOpen(false)}
        />
      )}
    </>
  );
};

export default UseCaseSelector;
